import type { Timestamp } from "firebase/firestore";

export type DateValue = Timestamp | Date | string | number | null | undefined;

export interface Product {
  id: string;
  schemaVersion?: number;
  name: string;
  sku?: string;
  description: string;
  brand: string;
  brandId?: string;
  category: string;
  categoryId?: string;
  price: number;
  sale: number;
  rating: number;
  reviewCount: number;
  popularity: number;
  stock: number;
  sizeStock?: Record<string, number>;
  active: boolean;
  archived?: boolean;
  featured?: boolean;
  color: string[];
  primaryColor?: string;
  colorVariantGroupId?: string;
  size: string[];
  imageId?: string;
  imageIds: string[];
  additional_photos?: string[];
  createdAt?: DateValue;
  updatedAt?: DateValue;
}

export interface OrderedProduct {
  productId?: string;
  productName?: string;
  imageId?: string;
  unitPrice?: number;
  quantity?: number;
  colorIndex?: number;
  colorName?: string;
  sizeIndex?: number;
  sizeName?: string;
  customSize?: string;
  productData?: Partial<Product>;
}

export interface StatusHistoryEntry {
  status: number;
  timestamp: DateValue;
  note?: string;
  changedBy?: string;
}

export interface Order {
  id: string;
  orderNumber?: string;
  userId?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  addressAdditionals?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  information?: string;
  products?: string;
  items?: OrderedProduct[];
  totalPrice?: number;
  currency?: string;
  paymentMethod?: string;
  deliveryMethod?: "courier" | "store_pickup";
  pickupStoreName?: string;
  pickupAddress?: string;
  pickupInstructions?: string;
  orderStatus?: number;
  createdAt?: DateValue;
  updatedAt?: DateValue;
  statusHistory?: StatusHistoryEntry[];
}

export interface Address {
  id?: string;
  label?: string;
  fullName?: string;
  phone?: string;
  city?: string;
  postalCode?: string;
  street?: string;
  isDefault?: boolean;
}

export interface Customer {
  id: string;
  username?: string;
  email?: string;
  phone?: string;
  defaultAddress?: string;
  addresses?: Address[];
  orders?: string[];
  wishlist?: string[];
  adminNotes?: string;
  customerStatus?: "active" | "vip" | "blocked";
  tags?: string[];
  createdAt?: DateValue;
  lastOrderAt?: DateValue;
}

export interface CatalogueEntity {
  id: string;
  name: string;
  slug: string;
  description?: string;
  imageId?: string;
  active: boolean;
  sortOrder: number;
  createdAt?: DateValue;
  updatedAt?: DateValue;
}

export interface Promotion {
  id: string;
  name: string;
  Heading: string;
  Body: string;
  Percent: string;
  code?: string;
  discountPercent?: number;
  active: boolean;
  startsAt?: DateValue;
  endsAt?: DateValue;
  bannerImageId?: string;
  appliesTo?: "all" | "products" | "categories" | "brands";
  targetIds?: string[];
  notifyCustomers?: boolean;
  notificationSentAt?: DateValue;
  createdAt?: DateValue;
  updatedAt?: DateValue;
}

export interface HomeContent {
  id: "home";
  eyebrow: string;
  title: string;
  subtitle: string;
  buttonLabel: string;
  actionType: "catalog" | "category" | "product" | "none";
  actionValue?: string;
  imageId?: string;
  active: boolean;
  updatedAt?: DateValue;
}

export interface StoreConfig {
  id: "store";
  pickupEnabled: boolean;
  storeName: string;
  address: string;
  city: string;
  postalCode: string;
  phone: string;
  openingHours: string;
  pickupInstructions: string;
  updatedAt?: DateValue;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName?: string;
  role: "owner" | "manager" | "support";
  active: boolean;
  createdAt?: DateValue;
  updatedAt?: DateValue;
}

export type PageKey =
  | "dashboard"
  | "analytics"
  | "orders"
  | "products"
  | "customers"
  | "catalogue"
  | "marketing"
  | "admins";

export const ORDER_STATUSES = [
  { value: 0, label: "Приета", tone: "purple" },
  { value: 1, label: "Потвърдена", tone: "blue" },
  { value: 4, label: "Подготвя се", tone: "amber" },
  { value: 5, label: "Изпратена", tone: "cyan" },
  { value: 6, label: "В доставка", tone: "cyan" },
  { value: 3, label: "Очаква получаване", tone: "amber" },
  { value: 7, label: "Доставена", tone: "green" },
  { value: 8, label: "Отказана", tone: "red" },
  { value: 9, label: "Върната", tone: "red" },
] as const;

export const PRODUCT_SIZES = [
  "XXS",
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
  "XXXL",
  "One Size",
  "Custom",
] as const;

export const PRODUCT_COLORS = [
  { value: "Black", label: "Черно", hex: "#171717" },
  { value: "White", label: "Бяло", hex: "#ffffff" },
  { value: "Gray", label: "Сиво", hex: "#9ca3af" },
  { value: "Beige", label: "Бежово", hex: "#dbc6a6" },
  { value: "Brown", label: "Кафяво", hex: "#7c4a2d" },
  { value: "Red", label: "Червено", hex: "#dc2626" },
  { value: "Bordeaux", label: "Бордо", hex: "#7f1d3a" },
  { value: "Pink", label: "Розово", hex: "#f472b6" },
  { value: "Purple", label: "Лилаво", hex: "#9333ea" },
  { value: "Blue", label: "Синьо", hex: "#2563eb" },
  { value: "Navy", label: "Тъмносиньо", hex: "#172554" },
  { value: "Green", label: "Зелено", hex: "#16a34a" },
  { value: "Yellow", label: "Жълто", hex: "#facc15" },
  { value: "Orange", label: "Оранжево", hex: "#f97316" },
  { value: "Gold", label: "Златисто", hex: "#d4a72c" },
  { value: "Silver", label: "Сребристо", hex: "#c0c0c0" },
  { value: "Multicolor", label: "Многоцветно", hex: "linear-gradient(135deg,#ef4444,#eab308,#22c55e,#3b82f6,#a855f7)" },
] as const;
